import { Icon, InputAdornment, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import NumberFormat from "react-number-format";
import {
  Alert,
  AlertAccount,
  CustomButton,
  DefaultLoader,
  Dropdown,
  InvestorInfo,
  InvestorCard,
} from "../../components";

import {
  getFundList,
  sendFundsCode,
  createFundV2,
  getScoreList,
} from "../fundSelection/redux/action";

import { getResultAssessment } from "../riskAssessment/redux/action";

import { setLastAmountInvest } from "../fundSelectionStep2/redux/actions";
import { useEcddChecking } from "../../utils/hooks";
import { NOT_ALLOWED_ACCOUNT_STATUS } from "../../constants/subaccount-status";
import { getSubAccountInvest } from "../dashboard/redux/actions";
import {
  getBoosterPlanMinAmount,
  getAdvancePlanMinAmount,
  getMixedFunds,
  getEquityFunds,
  getImmfFund,
} from "./redux/actions";
import { determineFundRisk } from "../../utils/functions/determineFundRisk";
import { showError } from "../../globalRedux/actions";

import "./style.plan.scss";

const plans = ["powersaver", "supercharge"];

function divideEvenly(n) {
  let base = Math.floor((n / 3) * 100) / 100;
  let first = n - 2 * base;
  return [first, base, base];
}

const liveAgentWhatsappNumber =
    process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER?.trim();
const liveAgentWhatsappNumberException1 =
    process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER_EXCEPTION_1?.trim();
function distributeAmountToFund(funds, amounts) {
  return Object.values(funds)
    .map((v, idx) =>
      v
        ? {
            amountCcy: "MYR",
            amount: amounts[idx],
            fundCd: v.code,
            name: v.name,
            fundInfoUrl: v.fundInfoUrl,
            salesCharge: v.salesFeePerc,
            serviceTaxPerc: v.serviceTaxPerc,
          }
        : null
    )
    .filter(Boolean);
}

const NumberFormatFixedDecimal = ({ inputRef, onChange, ...other }) => {
  return (
    <NumberFormat
      {...other}
      thousandSeparator
      decimalScale={2}
      fixedDecimalScale
      getInputRef={inputRef}
      displayType="input"
      onValueChange={values => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
    />
  );
};


const NumberFormatFixedNoDecimal = ({ inputRef, onChange, ...other }) => {
  return (
    <NumberFormat
      {...other}
      thousandSeparator
      decimalScale={0}
      fixedDecimalScale
      getInputRef={inputRef}
      displayType="input"
      onValueChange={values => {
        onChange({
          target: {
            value: values.value,
          },
        });
      }}
    />
  );
};

function Plan({
  history,
  match: { params },
  location,
  token,
  getFundList,
  fundList,
  profile,
  fundListLoading,
  sendFundsCode,
  setLastAmountInvest,
  lastInvestAmount,
  createFundV2,
  createFundLoadingV2,
  ecddStatus,
  getSubAccountInvest,
  subaccountInvest,
  subaccountInvestLoading,
  getBoosterPlanMinAmount,
  getAdvancePlanMinAmount,
  getEquityFunds,
  getMixedFunds,
  getImmfFund,
  equityFunds,
  equityFundsLoading,
  mixedFunds,
  mixedFundsLoading,
  immfFund,
  immfFundLoading,
  boosterPlanMinAmount,
  boosterPlanMinAmountLoading,
  advancePlanMinAmount,
  advancePlanMinAmountLoading,
  scoreList,
  scoreListLoading,
  getScoreList,
  riskProfileResult,
  resultAssessmentLoading,
  showError,
  getResultAssessment,
}) {
  const plan = params.plan;
  const currentPlan = JSON.parse(localStorage.getItem("currentPlan") || "null");

  const isSamePlan = currentPlan && currentPlan?.plan === plan;

  const [amount, setAmount] = useState(() =>
    isSamePlan && lastInvestAmount ? lastInvestAmount : ""
  );
  const [funds, setFunds] = useState({
    mixed: [],
    equity: [],
  });
  const [selectedFunds, setSelectedFunds] = useState(() => {
    if (isSamePlan) return currentPlan?.funds;

    return {};
  });
  const [accountStatus, setAccountStatus] = useState("");
  const [isRiskProfileExpired, setRiskProfileExpired] = useState(false);

  const [
    investInHigherFundWarning,
    setPromptInvestInHigherFundWarning,
  ] = useState(false);

  const [showInvestorLevel, setShowInvestorLevel] = useState(false);

  const { handleCheckEcdd, status, onResetStatus } = useEcddChecking({
    ecddStatus,
    aml: profile &&
      profile.amlHistory.length > 0 &&
      typeof profile.amlHistory[0] === "object" && {
        result: profile.amlHistory[0].amlResult,
        status: profile.amlHistory[0].amlSts,
      },
  });

  const isAdvance = plan === "supercharge";

  const MIN_AMOUNT = boosterPlanMinAmount;
  const MAX_AMOUNT = advancePlanMinAmount;

  const dropdownType = isAdvance ? "equity" : "mixed";

  const [dropdown, setDropdown] = useState({
    open: false,
    type: dropdownType,
  });
  const [formStatus, setFormStatus] = React.useState("idle");

  const [risk, setRisk] = useState({ level: "", index: 0, desc: "" });

  const isSubmitting = formStatus === "submitting";

  const distributedAmount = useMemo(() => {
    return isAdvance
      ? divideEvenly(Number(amount))
      : new Array(2).fill(Number(amount) / 2);
  }, [isAdvance, amount]);

  const lessThan = Boolean(amount < +MIN_AMOUNT);
  const biggerThan = Boolean(amount > +MAX_AMOUNT);

  const amountError = lessThan || biggerThan;

  const handleCheckAccountStatus = subaccountStatus => {
    if (NOT_ALLOWED_ACCOUNT_STATUS.includes(profile?.headAccount?.sts)) {
      setAccountStatus(profile.headAccount.sts);
      return "blocked";
    }

    if (NOT_ALLOWED_ACCOUNT_STATUS.includes(subaccountStatus)) {
      setAccountStatus(subaccountStatus);
      return "blocked";
    }
  };

  const handleSubmit = () => {
    setFormStatus("submitting");

    if (
      lessThan ||
      biggerThan ||
      (isAdvance &&
        (!selectedFunds?.equity?.name || !selectedFunds?.mixed?.name)) ||
      (plan === "powersaver" && !selectedFunds?.mixed?.name) ||
      !funds.immf
    )
      return;

    if (
      new Date() >=
      moment(riskProfileResult?.expiredDate)
        .startOf("date")
        .toDate()
    ) {
      setRiskProfileExpired(true);
      return;
    }

    const currentAccountStatus = handleCheckAccountStatus(
      subaccountInvest?.sts
    );

    if (currentAccountStatus === "blocked") return;

    const someFundHasHighRisk = [
      funds.immf,
      selectedFunds?.mixed,
      selectedFunds.equity,
    ].some(fund => {
      const risk = determineFundRisk({
        codeRange: fund?.riskProfileCode,
        currentCodeRange: riskProfileResult?.result,
        scoreList,
      });

      return risk === "HIGH_RISK";
    });

    if (someFundHasHighRisk) {
      return setPromptInvestInHigherFundWarning(true);
    }

    handlePostSubmit();
  };

  const handlePostSubmit = () => {
    const distributedFunds = distributeAmountToFund(
      {
        immf: funds?.immf,
        mixed: selectedFunds?.mixed,
        equity: selectedFunds?.equity,
      },
      distributedAmount
    );

    const redirectCallback = cifPlanId => subaccount => {
      if (!subaccount?.sts || !cifPlanId) return;

      const currentAccountStatus = handleCheckAccountStatus(subaccount.sts);

      if (currentAccountStatus === "blocked") return;

      sendFundsCode(distributedFunds);

      // for maintaining selected funds values when user decided to click back
      // if user succesfully cash in, we remove this
      localStorage.setItem(
        "currentPlan",
        JSON.stringify({
          funds: selectedFunds,
          plan,
        })
      );

      setLastAmountInvest(amount);

      history.push("/plan/summary", {
        cifPlanId,
      });
    };

    createFundV2(
      {
        cifId: profile.masterId,
        fundsPayload: distributedFunds.map(({ fundCd }) => ({
          fundCode: fundCd,
        })),
      },
      portfolio =>
        getSubAccountInvest(
          portfolio?.accountNo,
          redirectCallback(portfolio?.cifPlanId)
        )
    );
  };

  const handleChange = e => {
    const value = e.target.value;
    if (value === "" || (!isNaN(value) && Number(value) >= 0)) {
      setAmount(value);
    }
  };

  const handlePaste = event => {
    const pasteData = event.clipboardData.getData("text");
    if (pasteData.includes("-")) {
      event.preventDefault();
    }
  };

  const handleKeyDown = event => {
    if (event.key === "-") {
      event.preventDefault();
    }
  };

  const onSelectFund = fund => {
    setSelectedFunds(prev => ({
      ...prev,
      [dropdown.type]: {
        name: fund.name,
        code: fund.code,
        fundInfoUrl: fund.fundInfoUrl,
        riskProfileCode: fund.riskProfileCode,
        salesFeePerc: fund.salesFeePerc,
        serviceTaxPerc: fund.serviceTaxPerc,
      },
    }));

    setDropdown({ open: false, type: dropdownType });
  };

  const findFunds = codes => {
    const map = new Map(fundList.map(obj => [obj.code, obj]));

    const filtered = (codes || []).map(fundCd => {
      const fund = map.get(fundCd);

      if (!fund) return null;

      return fund;
    }).filter(Boolean);

    return filtered;
  };

  useEffect(() => {
    const cachedSelectedFunds = selectedFunds;

    if (Object.values(cachedSelectedFunds).length > 0) return;

    if (funds?.mixed?.length > 0 && funds?.equity?.length > 0) {
      const mixedFund = funds.mixed[0];
      const equityFund = funds.equity[0];

      if (history?.location?.pathname?.includes("supercharge")) {
        setSelectedFunds({
          mixed: {
            name: mixedFund.name,
            code: mixedFund.code,
            fundInfoUrl: mixedFund.fundInfoUrl,
            riskProfileCode: mixedFund.riskProfileCode,
            salesFeePerc: mixedFund.salesFeePerc,
            serviceTaxPerc: mixedFund.serviceTaxPerc,
          },
          equity: {
            name: equityFund.name,
            code: equityFund.code,
            fundInfoUrl: equityFund.fundInfoUrl,
            riskProfileCode: equityFund.riskProfileCode,
            riskProfileDesc: equityFund.riskProfileDesc,
            salesFeePerc: equityFund.salesFeePerc,
            serviceTaxPerc: equityFund.serviceTaxPerc,
          },
        });
      } else {
        setSelectedFunds({
          mixed: {
            name: mixedFund.name,
            code: mixedFund.code,
            fundInfoUrl: mixedFund.fundInfoUrl,
            riskProfileCode: mixedFund.riskProfileCode,
            salesFeePerc: mixedFund.salesFeePerc,
            serviceTaxPerc: mixedFund.serviceTaxPerc,
          },
        });
      }
    }
  }, [funds, history?.location?.pathname]);

  useEffect(() => {
    getFundList();
    getMixedFunds();
    getEquityFunds();
    getImmfFund();
    getScoreList();
    getBoosterPlanMinAmount();
    getAdvancePlanMinAmount();

    const currentPlan = JSON.parse(
      localStorage.getItem("currentPlan") || "null"
    );
    if (currentPlan && currentPlan?.plan !== plan) {
      localStorage.removeItem("currentPlan");
    }
  }, []);

  // On session-expired resume, Plan stays mounted on the same path so the
  // mount effect above never re-runs and the fund-list APIs are never called
  // again. Detect the token renewal (old token -> new token) and refetch the
  // fund list so the plan dropdowns are not left empty after re-auth.
  const prevTokenRef = useRef(token);
  useEffect(() => {
    const prevToken = prevTokenRef.current;
    prevTokenRef.current = token;
    if (prevToken && token && prevToken !== token) {
      getFundList();
      getMixedFunds();
      getEquityFunds();
      getImmfFund();
      getScoreList();
      getBoosterPlanMinAmount();
      getAdvancePlanMinAmount();
    }
  }, [token]);


  useEffect(() => {
    if (location?.state?.justCompletedRiskAssessment) return;

    getResultAssessment();
  }, [location?.state?.justCompletedRiskAssessment]);

  useEffect(() => {
    if (scoreList.length > 0 && Object.entries(riskProfileResult).length > 0) {
      const riskNames = scoreList.reduce((prev, curr) => {
        prev[curr.nameEn.toLowerCase()] = curr.descEn;
        return prev;
      }, {});

      const level = riskProfileResult.resultNameEn.toLowerCase();

      setRisk(prev => ({
        ...prev,
        index: Object.keys(riskNames).indexOf(level),
        desc: riskNames[level],
        level: level,
      }));
    }
  }, [scoreList, riskProfileResult]);

  useEffect(() => {
    if (
      fundList?.length > 0 &&
      mixedFunds?.length > 0 &&
      equityFunds?.length > 0 &&
      immfFund
    ) {
      setFunds({
        equity: findFunds(equityFunds),
        mixed: findFunds(mixedFunds),
        immf: fundList.find(
          ({ code, name, fundInfoUrl, riskProfileCode, salesFeePerc }) =>
            code === immfFund
              ? {
                  fundCd: code,
                  name,
                  fundInfoUrl,
                  riskProfileCode,
                  salesFeePerc: salesFeePerc,
                }
              : null
        ),
      });
    }
  }, [fundList, equityFunds, mixedFunds, immfFund]);

  if (!plans.includes(plan)) return null;

  if (
    fundListLoading ||
    createFundLoadingV2 ||
    subaccountInvestLoading ||
    equityFundsLoading ||
    mixedFundsLoading ||
    boosterPlanMinAmountLoading ||
    advancePlanMinAmountLoading ||
    resultAssessmentLoading ||
    scoreListLoading ||
    immfFundLoading
  )
    return <DefaultLoader />;

  return (
    <>
      <Alert
        title="Your risk profile is expired"
        description="Risk Profile has expired, please perform Risk Assessment"
        isOpen={isRiskProfileExpired}
        isCancel
        btnTxt="Ok"
        handleAction={() => {
          history.push("/invest-introduction", {
            expired: true,
            plan,
          });
        }}
        handleCancel={() => setRiskProfileExpired(false)}
      />
      <AlertAccount
        title="You’ve selected a fund which does not match your risk profile. Do you wish to continue?"
        firstText="The fund you have selected does not match your risk profile. If you wish to continue, please note on the additional risk you might be exposed to."
        isOpen={investInHigherFundWarning}
        hideDoneButton
        firstTextDialogStyle={{ marginTop: "0.5rem", paddingBottom: "0" }}
        handleClose={() => setPromptInvestInHigherFundWarning(false)}
        titleStyle={{
          fontSize: "1.25rem",
          fontWeight: "700",
          lineHeight: "28px",
        }}
      >
        <div>
          <CustomButton
            variant="contained"
            text="Yes, Continue"
            color="#fff"
            backgroundColor="#1278CC"
            onClick={() => {
              setPromptInvestInHigherFundWarning(false);

              handlePostSubmit();
            }}
          />
          <CustomButton
            variant="contained"
            text="Maybe other fund"
            color="#1278CC"
            backgroundColor="transparent"
            onClick={() => setPromptInvestInHigherFundWarning(false)}
            style={{ boxShadow: "none", marginTop: "0.5rem" }}
          />
        </div>
      </AlertAccount>
      <AlertAccount
        title={`Account ${
          accountStatus === "Suspended" ? "Suspended" : "Inactive"
        }`}
        firstText={
          accountStatus === "Suspended"
            ? "Your account is currently suspended, we are unable to process your Cash In request however, you can still proceed with Cash Out."
            : "Your account is currently inactive, we're sorry for the inconvenience."
        }
        secondText={
          <span>
            For assistance, kindly contact our{" "}
            <span
              onClick={() => {
                setAccountStatus("");

                window.my.call("openAppLink", {
                  url: `https://wa.me/${liveAgentWhatsappNumberException1}`,
                });
              }}
              style={{ color: "#1278CC" }}
            >
              Customer Care Centre.
            </span>
          </span>
        }
        isOpen={NOT_ALLOWED_ACCOUNT_STATUS.includes(accountStatus)}
        handleClose={() => setAccountStatus("")}
      />
      <Alert
        title="Account Verification"
        description="Thank you for submitting your information. We need to perform further verification to activate your account."
        isOpen={status === "pending"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: `https://wa.me/${liveAgentWhatsappNumberException1}`,
          });
        }}
        handleCancel={onResetStatus}
      />
      <Alert
        title="Your account is deactivated"
        description="So sorry, your account is deactivated. We need to perform a verification to activate your account."
        isOpen={status === "rejected"}
        isCS="https://principal.com.my"
        isCancel={false}
        btnTxt="Chat with us on WhatsApp"
        handleAction={() => {
          onResetStatus();
          window.my.call("openAppLink", {
            url: `https://wa.me/${liveAgentWhatsappNumberException1}`,
          });
        }}
        handleCancel={onResetStatus}
      />

      <InvestorCard
        riskIndex={risk.index}
        riskLevel={risk.level}
        desc={risk.desc}
        handleSubmit={() => {
          setShowInvestorLevel(false);
        }}
        onClose={() => {
          setShowInvestorLevel(false);
        }}
        isOpen={showInvestorLevel}
        buttonCopywriting="Ok"
        isPopup
      />

      <Dropdown
        snapPoints={({ maxHeight }) => maxHeight - maxHeight / 28}
        isOpen={dropdown.open}
        handleDismiss={() => setDropdown({ open: false, type: "" })}
      >
        <div style={{ margin: "16px 16px" }}>
          <Typography
            color="secondary"
            style={{
              fontFamily: "FSElliotPro",
              fontSize: "20px",
              fontWeight: "700",
              lineHeight: 1.4,
              marginBottom: "16px",
            }}
          >
            Please select fund for investment
          </Typography>
          {funds[dropdown.type]?.map((item, index) => (
            <div
              key={index}
              onClick={() => onSelectFund(item)}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 0px 14px",
              }}
            >
              <Typography
                color="textPrimary"
                style={{
                  fontFamily: "FSElliotPro",
                  fontSize: "16px",
                  lineHeight: 1.5,
                  fontWeight: "700",
                  letterSpacing: "0.32px",
                }}
              >
                {item?.name} - {item?.riskProfileDesc}
              </Typography>
            </div>
          ))}
        </div>
      </Dropdown>

      <form className="l-plan">
        <div>
          <h1 className="l-plan__title">
            Upgrade to {isAdvance ? "Supercharge" : "Power Saver"} plan
          </h1>
          <p className="l-plan__desc">
            Diversify your investment portfolio by Investing equal amounts
            across multiple funds in Principal's 'Invest' feature
          </p>
          <InvestorInfo
            handleClick={() => {
              setShowInvestorLevel(true);
            }}
            riskLevel={risk.level}
            riskIndex={risk.index}
            containerStyle={{
              border: "1px solid #E7E7E7",
              borderRadius: "5px",
              padding: "0.8rem",
              margin: "0",
            }}
          />

          <h1 className="l-plan__sub-title">Amount you want to invest</h1>

          <TextField
            fullWidth
            variant="outlined"
            placeholder={Number(MIN_AMOUNT).toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
            value={amount === "0" ? "" : amount}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            error={isSubmitting && (lessThan || biggerThan)}
            helperText={
              isSubmitting &&
              (lessThan
                ? `Minimum RM ${Number(MIN_AMOUNT).toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}`
                : biggerThan &&
                  `The maximum Cash In amount per transaction is RM ${Number(
                    MAX_AMOUNT
                  ).toLocaleString("en-US", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}. You may proceed with your additional Cash In into multiple transactions`)
            }
            FormHelperTextProps={{
              className: "c-input__text",
            }}
            inputProps={{
              className: "c-input c-input--outline",
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              inputComponent: NumberFormatFixedNoDecimal,
              className: "c-input__wrapper",

              startAdornment: (
                <InputAdornment position="start">
                  <p style={{ color: "#ccccc" }}>RM</p>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="l-plan__funds">
          <div className="l-plan__product-wrapper">
            <h2 className="l-plan__product">For Savings</h2>
            <p className="l-plan__product-desc">
              Invested in {funds?.immf?.name}.
            </p>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="0.00"
              value={distributedAmount[0]}
              disabled
              FormHelperTextProps={{
                className: "c-input__text",
              }}
              inputProps={{
                className: "c-input c-input--outline",
                readOnly: true,
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                inputComponent: NumberFormatFixedDecimal,
                className: "c-input__wrapper--no-border",
                style: {
                  backgroundColor: "#f6f6f8",
                },

                startAdornment: (
                  <InputAdornment position="start">
                    <p className="c-input__start-adornment-text">RM</p>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="l-plan__product-wrapper">
            <h2 className="l-plan__product">For Investment</h2>
            <p className="l-plan__product-desc">
              Select one Fixed Income, Bond or Mixed Asset Fund
            </p>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Select fund"
              value={selectedFunds?.mixed?.name || ""}
              error={
                isSubmitting && !amountError && !selectedFunds?.mixed?.name
              }
              helperText={
                isSubmitting &&
                !amountError &&
                !selectedFunds?.mixed?.name &&
                "Please select fund"
              }
              onClick={() => setDropdown({ open: true, type: "mixed" })}
              FormHelperTextProps={{
                className: "c-input__text",
              }}
              inputProps={{
                className:
                  "c-input c-input--outline c-input--ellipsis c-input--dropdown",
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                className: "c-input__wrapper",
                readOnly: true,
                endAdornment: (
                  <Icon
                    color="primary"
                    style={{
                      color: "#0091DA",
                    }}
                  >
                    expand_more
                  </Icon>
                ),
              }}
            />
            <TextField
              fullWidth
              variant="outlined"
              placeholder="0.00"
              value={distributedAmount[1]}
              disabled
              FormHelperTextProps={{
                className: "c-input__text",
              }}
              inputProps={{
                className: "c-input c-input--outline",
                readOnly: true,
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              InputProps={{
                inputComponent: NumberFormatFixedDecimal,
                className: "c-input__wrapper--no-border",
                style: {
                  backgroundColor: "#f6f6f8",
                  marginTop: "12px",
                },

                startAdornment: (
                  <InputAdornment position="start">
                    <p className="c-input__start-adornment-text">RM</p>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          {isAdvance && (
            <div className="l-plan__product-wrapper">
              <p className="l-plan__product-desc">Equity Fund</p>
              <p className="l-plan__product-name">{selectedFunds?.equity?.name} - {selectedFunds?.equity?.riskProfileDesc}</p>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="0.00"
                value={distributedAmount[2]}
                disabled
                FormHelperTextProps={{
                  className: "c-input__text",
                }}
                inputProps={{
                  className: "c-input c-input--outline",
                  readOnly: true,
                }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{
                  inputComponent: NumberFormatFixedDecimal,
                  className: "c-input__wrapper--no-border",
                  style: {
                    backgroundColor: "#f6f6f8",
                  },

                  startAdornment: (
                    <InputAdornment position="start">
                      <p className="c-input__start-adornment-text">RM</p>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
        </div>

        <div>
          <CustomButton
            text="Continue"
            variant="contained"
            color="#fff"
            backgroundColor="#1278CC"
            onClick={handleCheckEcdd({
              cb: handleSubmit,
              ecddExpiredCb: () =>
                history.push("/onBoarding/ecdd/DEEPLINK", params),
              gtm: {
                event: "click_tag",
                action: "Click",
                category: "Button",
                label: "Cash In Button - Plan",
              },
            })}
          />
        </div>
      </form>
    </>
  );
}

const withConnect = connect(
  state => ({
    profile: state.goal.profile,
    token: state.auth.MPtoken,
    fundList: state.fundSelectionReducer.fundList,
    fundListLoading: state.fundSelectionReducer.fundListLoading,
    lastInvestAmount: state.investPortfolioReducer.lastInvestAmount,
    createFundLoadingV2: state.fundSelectionReducer.createFundLoadingV2,
    ecddStatus: state.goal.ecddStatus,
    subaccountInvestLoading: state.dashboardReducer.subAccStsInvestLoading,
    subaccountInvest: state.dashboardReducer.subAccStsInvest,
    equityFunds: state.planReducer.equityFunds,
    equityFundsLoading: state.planReducer.equityFundsLoading,
    mixedFunds: state.planReducer.mixedFunds,
    mixedFundsLoading: state.planReducer.mixedFundsLoading,
    immfFund: state.planReducer.immfFund,
    immfFundLoading: state.planReducer.immfFundLoading,
    advancePlanMinAmount: state.planReducer.advancePlanMinAmount,
    advancePlanMinAmountLoading: state.planReducer.advancePlanMinAmountLoading,
    boosterPlanMinAmount: state.planReducer.boosterPlanMinAmount,
    boosterPlanMinAmountLoading: state.planReducer.boosterPlanMinAmountLoading,
    riskProfileResult: state.riskAssessmentReducer.riskProfileResult,
    resultAssessmentLoading:
      state.riskAssessmentReducer.resultAssessmentLoading,
    scoreList: state.fundSelectionReducer.scoreList,
    scoreListLoading: state.fundSelectionReducer.scoreListLoading,
  }),
  {
    getFundList,
    sendFundsCode,
    setLastAmountInvest,
    createFundV2,
    getSubAccountInvest,
    getBoosterPlanMinAmount,
    getAdvancePlanMinAmount,
    getMixedFunds,
    getEquityFunds,
    getImmfFund,
    getScoreList,
    getResultAssessment,
    showError,
  }
);

export default compose(
  withRouter,
  withConnect
)(Plan);
